import React, { useEffect, useMemo } from 'react';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Flex, Skeleton, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useReduxDispatch } from '../../../app/store';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { selectEvChargerData } from '../dashboard/evChargerSlice';
import { EditSchedule } from './EditSchedule';
import { ScheduleCard } from './ScheduleCard';
import { addSchedule, editSchedule, fetchEvChargerSchedules, selectEvChargerSchedules } from './schedulerSlice';

/* istanbul ignore next -- @preserve */
export function ScheduleList() {
  const dispatch = useReduxDispatch();
  const { t } = useTranslation();
  const { siteDeviceId } = useSelector(selectEvChargerData);
  const { isError, isLoaded, schedules } = useSelector(selectEvChargerSchedules);
  const isEditing = useMemo(() => schedules.some((schedule) => schedule.isEditing), [schedules]);

  useEffect(() => {
    if (siteDeviceId) {
      dispatch(fetchEvChargerSchedules(siteDeviceId));
    }
  }, [siteDeviceId, dispatch]);

  function handleAddSchedule() {
    dispatch(
      addSchedule({
        groupId: null,
        name: null,
        daysOfWeek: [],
        startTime: null,
        endTime: null,
        isEditing: true,
      })
    );
  }

  function handleEditSchedule(groupId: string) {
    dispatch(editSchedule(groupId));
  }

  if (isError) {
    return (
      <Box mt={4} w="100%">
        <Alert status="error" variant="left-accent" data-testid="scheduler-error-alert">
          <AlertIcon />
          <Box>
            <AlertTitle>{t('Common.notice')}</AlertTitle>
            <AlertDescription>
              {t('Devices.error fetching schedules')}.{' '}
              {`${t('Common.please try again')} ${t('Common.if this persists contact support')}`}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box mt={6} w="100%">
      <Flex mb={3} borderBottom="1px solid" borderColor="#C6C6C6" align="Center" justify="space-between">
        <Box>{isEditing ? t('EVSE.schedule charge').toUpperCase() : t('EVSE.schedules').toUpperCase()}</Box>

        {!isEditing && (
          <Button
            data-testid="add-ev-charger-schedule"
            onClick={handleAddSchedule}
            size="sm"
            variant="ghost"
            colorScheme="schneiderSkyBlue"
            fontWeight={400}
            isDisabled={!isLoaded}
          >
            {t('Common.add')}
          </Button>
        )}
      </Flex>

      {isEditing ? (
        <EditSchedule siteDeviceId={siteDeviceId} />
      ) : (
        <Skeleton isLoaded={isLoaded}>
          {schedules.length ? (
            schedules.map(
              (schedule) =>
                schedule.groupId && (
                  <ScheduleCard key={schedule.groupId} groupId={schedule.groupId} onEdit={handleEditSchedule} />
                )
            )
          ) : (
            <SEHomeCard data-testid="empty-schedules-card">
              <Text color="#8A8A8D" my={2}>
                {t('Devices.empty schedules text')}
              </Text>
            </SEHomeCard>
          )}
        </Skeleton>
      )}
    </Box>
  );
}
